import { Injectable } from '@angular/core';

import { LANGUAGE } from './language';

@Injectable({
  providedIn: 'root'
})
export class NumberLocalizationService {
  private static formatToCustomParts(
    value: number, localeId: string | undefined, options?: Intl.NumberFormatOptions): Intl.NumberFormatPart[] {
    if ((localeId ?? 'zz') === 'zz') {
      localeId = 'en-CA';
    }

    return Intl.NumberFormat(localeId, options)
      .formatToParts(value)
      .map(part => {
        // Set the group separator for English (Canada) to no-break space
        if (localeId === LANGUAGE.ENGLISH_CANADA as string && part.type === 'group') {
          part.value = '\u00A0';
        }

        return part;
      });
  }

  public static format(value: number, localeId: string): string {
    // TODO: Replace with { useGrouping: 'min2' } when it becomes available (TypeScript 5.5+)
    const options = { useGrouping: (Math.abs(value) >= 10000) };

    return this.formatToCustomParts(value, localeId, options)
      // Replace all space characters with non-breaking spaces to prevent a number from being broken in 2 lines
      .map(part => part.value.replace(/\s/g, '\u00A0'))
      .reduce((formattedNumber, currentPart) => formattedNumber + currentPart, '');
  }

  public static parse(text: string, localeId: string | undefined): number {
    if (!text) {
      return Number.NaN;
    }

    const parts = this.formatToCustomParts(12345.67, localeId);
    const groupSeparator = parts.find(part => part.type === 'group')?.value.replace(/\s/g, ' ') ?? '';
    const decimalSeparator = parts.find(part => part.type === 'decimal')?.value ?? '';

    const simpleNumberPattern = `\\d+|\\d+\\${decimalSeparator}\\d+`;
    const groupedIntegerPattern = `(?:\\d{1,3}\\${groupSeparator})+\\d{3}`;
    const groupedDecimalPattern = `${groupedIntegerPattern}\\${decimalSeparator}\\d+`;

    const numberRegex = new RegExp(`^[-+]?(?:${simpleNumberPattern}|${groupedIntegerPattern}|${groupedDecimalPattern})$`);
    if (!numberRegex.test(text)) {
      return Number.NaN;
    }

    const result = Number(text.replace(new RegExp(`\\${groupSeparator}`, 'g'), '').replace(decimalSeparator, '.'));

    return result;
  }

  public static isInteger(text: string, localeId: string | undefined): boolean {
    const parsedNumber = this.parse(text, localeId);

    const parts = this.formatToCustomParts(1.2, localeId);
    const decimalSeparator = parts.find(part => part.type === 'decimal')?.value ?? '';

    return Number.isInteger(parsedNumber) && !text.includes(decimalSeparator);
  }

  public static getDecimalSeparator(locale: string = 'en-CA'): string {
    const parts = new Intl.NumberFormat(locale).formatToParts(1.1);
    const decimal = parts.find(p => p.type === 'decimal');
    return decimal?.value ?? '.';
  } 
}
